<template>
  <div class="order">
    <van-nav-bar fixed title="我的订单" left-text="返回" left-arrow @click-left="$router.go(-1)" />

    <!-- 自定义标签页 -->
    <div class="custom-tabs">
      <div
        class="tab-item"
        :class="{ active: active === 'all' }"
        @click="switchTab('all')"
      >全部</div>
      <div
        class="tab-item"
        :class="{ active: active === 'payment' }"
        @click="switchTab('payment')"
      >待支付</div>
      <div
        class="tab-item"
        :class="{ active: active === 'delivery' }"
        @click="switchTab('delivery')"
      >待发货</div>
      <div
        class="tab-item"
        :class="{ active: active === 'received' }"
        @click="switchTab('received')"
      >待收货</div>
      <div
        class="tab-item"
        :class="{ active: active === 'comment' }"
        @click="switchTab('comment')"
      >待评价</div>
      <div
        class="tab-item"
        :class="{ active: active === 'refund' }"
        @click="switchTab('refund')"
      >退款</div>
    </div>

    <!-- 退款记录列表 -->
    <template v-if="active === 'refund'">
      <div v-if="refundList.length > 0" class="refund-list">
        <div class="refund-card" v-for="r in refundList" :key="r.id">
          <div class="rf-head">
            <span class="rf-no">订单号：{{ r.order_no }}</span>
            <span class="rf-status" :class="'s' + r.status">{{ r.status_text }}</span>
          </div>
          <div class="rf-row">退款金额：<b>¥{{ r.amount }}</b></div>
          <div class="rf-row">申请时间：{{ r.apply_time }}</div>
          <div class="rf-row">退款原因：{{ r.reason }}</div>
          <div class="rf-row" v-if="r.reply">客服回复：{{ r.reply }}</div>
        </div>
      </div>
      <div v-else class="empty-state">
        <span>暂无退款记录</span>
      </div>
    </template>

    <!-- 订单列表 -->
    <template v-else>
      <OrderListItem
        v-for="item in orderList"
        :key="item.order_id"
        :item="item"
        @expired="onOrderExpired"
        @confirm="onConfirmOrder"
        @comment="onOpenComment"
        @refund="onOpenRefund"
      />
    </template>

    <div class="footer">
      <!-- 分页 -->
      <div class="pagectrl" v-if="orderList.length > 0 && active !== 'refund'">
        <button v-if="nowPage > 1" class="btn" @click="changePage(-1)">上一页</button>
        <div class="pageNum">当前页:{{ nowPage }}</div>
        <button v-if="nowPage < lastPage" class="btn" @click="changePage(1)">下一页</button>
      </div>
      <!-- 空状态 -->
      <div class="empty" v-else-if="active !== 'refund'">
        <span>{{ StateObj[active] }}</span>
      </div>
    </div>

    <!-- 底部导航占位 -->
    <div class="bottom-placeholder"></div>
  </div>
</template>

<script>
import { getOrderListApi, confirmOrderApi, getMyRefundsApi } from '@/api/order'
import { getPaymentResultApi } from '@/api/pay'
import { Toast } from 'vant'
import OrderListItem from '@/components/OrderListItem.vue'
import { saveOrder } from '@/utils/orderTransfer'
import { readOrderTab } from '@/utils/orderTab'

export default {
  name: 'MyOrder',
  components: {
    OrderListItem
  },
  data () {
    return {
      // 当前选中的 tab（订单类型）
      active: 'all',
      // 订单列表
      orderList: [],
      // 退款记录
      refundList: [],
      // 当前页
      nowPage: 1,
      // 总页数
      lastPage: 1,
      // 微信自愈核对时间戳（用于 5 秒节流，避免切换 Tab/翻页时重复向微信查单）
      reconcileLockAt: 0,
      // 各类型空状态文案
      StateObj: {
        all: '暂无订单',
        payment: '暂无待支付订单',
        delivery: '暂无待发货订单',
        received: '暂无待收货订单',
        comment: '暂无待评价订单',
        refund: '暂无退款记录'
      }
    }
  },
  watch: {
    // 切换 tab 时重新拉取订单
    active () {
      this.nowPage = 1
      if (this.active === 'refund') {
        this.getRefundList()
      } else {
        this.getOrderList()
      }
    }
  },
  created () {
    // 页签来源：
    //   1) 兼容旧链接 /myorder?dataType=xxx
    //   2) 新跳转通过 sessionStorage 传递，地址栏保持干净的 /myorder，避免微信拦截带参数的订单链接
    const dataType = this.$route.query.dataType || readOrderTab()
    if (dataType) {
      this.active = dataType
    }
    // 历史链接若带 dataType，加载后立即规范化为 /myorder（保留支付成功提示参数）
    if (this.$route.query.dataType) {
      const query = this.$route.query.pay_success === '1' ? { pay_success: '1' } : {}
      this.$router.replace({ path: '/myorder', query })
    }
    // 支付宝支付完成同步回跳后提示
    if (this.$route.query.pay_success === '1') {
      this.$nextTick(() => {
        Toast.success('支付成功')
      })
    }
    if (this.active === 'refund') {
      this.getRefundList()
    } else {
      this.getOrderList()
    }
  },
  methods: {
    // 切换标签
    switchTab (type) {
      if (this.active !== type) {
        this.active = type
      }
    },
    // 获取订单列表
    async getOrderList () {
      const { data: { list, page, totalPage } } = await getOrderListApi(this.active, this.nowPage)
      this.orderList = list
      this.nowPage = page
      this.lastPage = totalPage
      // 自动核对待支付中的微信订单：微信侧若已扣款成功（回调延迟/丢失）则自动纠正为「待发货」，
      // 避免「微信支付成功但立即退出网站，再打开时订单仍显示待支付 / 可被重复支付」的问题
      if (this.active !== 'refund') {
        this.reconcileWechatPaid()
      }
    },
    // 主动向微信核对：订单是否其实已经支付成功
    // 无论当前停留在哪个 Tab（如支付成功后被跳到「待发货」Tab），都会检查最近 15 分钟内
    // 创建的「待支付」微信订单（最多 3 笔），微信侧若已扣款成功则自动纠正为待发货
    async reconcileWechatPaid () {
      const now = Date.now()
      // 5 秒内只核对一次，避免切换 Tab / 翻页时重复请求微信查单接口
      if (this.reconcileLockAt && now - this.reconcileLockAt < 5000) return
      this.reconcileLockAt = now
      try {
        // 需要核对的候选列表：当前列表中的待支付单
        const lists = [this.orderList || []]
        // 若当前不在「待支付」Tab 第 1 页（比如支付成功后默认落在「待发货」），
        // 再拉一次「待支付」第 1 页，保证微信已扣款但本地仍未更新的订单也能被发现
        if (this.active !== 'payment' || this.nowPage !== 1) {
          const { data } = await getOrderListApi('payment', 1)
          if (data && data.list) lists.push(data.list)
        }
        const seen = {}
        const pendings = []
        lists.forEach((list) => {
          (list || []).forEach((o) => {
            if (pendings.length >= 3 || seen[o.order_id]) return
            if (o.state_text !== '待支付') return
            const method = o.payment_method || ''
            // 微信支付过的订单；以及下单后尚未写入支付方式的订单（真实支付在异步通知到达前
            // payment_method 可能仍为空）都纳入核对，避免漏掉「已扣款但订单未更新」的场景
            if (method !== 'wechat' && method !== '') return
            const t = o.create_time ? new Date(String(o.create_time).replace(/-/g, '/')).getTime() : 0
            if (t <= 0 || (now - t) >= 15 * 60 * 1000) return
            seen[o.order_id] = 1
            pendings.push(o)
          })
        })
        if (pendings.length === 0) return

        const results = await Promise.all(
          pendings.map((o) => getPaymentResultApi(o.order_id, true).catch(() => null))
        )
        const changed = results.some((res) => res && res.data && (res.data.paid || res.data.status === 1))
        if (changed) {
          // 已有一笔支付成功但订单未更新：重新拉取当前 Tab 列表展示最新状态
          const { data } = await getOrderListApi(this.active, this.nowPage)
          this.orderList = data.list
          this.lastPage = data.totalPage || 1
          Toast('检测到已支付成功的订单，状态已更新')
        }
      } catch (e) {
        console.error('核对微信支付结果失败', e)
      }
    },
    // 获取退款记录
    async getRefundList () {
      const { data: { list } } = await getMyRefundsApi()
      this.refundList = list || []
    },
    // 订单支付超时：刷新列表（后端会删除超时订单）
    onOrderExpired (orderId) {
      this.getOrderList()
    },
    // 翻页
    changePage (num) {
      this.nowPage += num
      this.getOrderList()
    },
    // 确认收货
    onConfirmOrder (order) {
      if (!confirm(`确认已收到「${order.order_no}」的商品吗？确认后订单将完成。`)) return
      confirmOrderApi(order.order_id)
        .then(() => {
          Toast.success('确认收货成功')
          this.getOrderList()
        })
        .catch((e) => {
          Toast(typeof e === 'string' ? e : '确认收货失败，请重试')
        })
    },
    // 跳转到申请退款页面
    onOpenRefund (order) {
      saveOrder(order)
      this.$router.push('/refund')
    },
    // 跳转到订单评价页面
    onOpenComment (order) {
      saveOrder(order)
      this.$router.push('/comment')
    }
  }
}
</script>

<style lang="less" scoped>
.order {
  background-color: #fafafa;
  min-height: 100vh;
  padding-top: 46px; // 给固定导航栏留空间
  padding-bottom: 50px; // 给底部导航留空间

  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }
  /deep/ .van-nav-bar { z-index: 100; }

  // 自定义标签页
  .custom-tabs {
    display: flex;
    background: #ffffff;
    border-bottom: 1px solid #f0f0f0;
    position: sticky;
    top: 46px;
    z-index: 10;

    .tab-item {
      flex: 1;
      text-align: center;
      padding: 12px 0;
      font-size: 14px;
      color: #666;
      cursor: pointer;
      position: relative;
      transition: all 0.3s;

      &.active {
        color: #ff6b35;
        font-weight: 600;

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 20px;
          height: 3px;
          background: #ff6b35;
          border-radius: 2px;
        }
      }

      &:active {
        background: #f5f5f5;
      }
    }
  }

  // 退款记录
  .refund-list {
    padding: 10px 12px;

    .refund-card {
      background: #fff;
      border-radius: 8px;
      padding: 14px;
      margin-bottom: 10px;
      box-shadow: 0 0.5px 2px 0 rgba(0, 0, 0, 0.05);

      .rf-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;

        .rf-no {
          font-weight: 600;
          color: #333;
          font-size: 13px;
        }

        .rf-status {
          font-size: 12px;
          padding: 2px 10px;
          border-radius: 10px;
          background: #f0f0f0;
          color: #666;

          &.s0 { background: #fff3e0; color: #f57c00; }
          &.s1 { background: #e8f5e9; color: #4caf50; }
          &.s2 { background: #fce4ec; color: #f44336; }
        }
      }

      .rf-row {
        font-size: 13px;
        color: #666;
        line-height: 1.8;
        word-break: break-all;

        b { color: #ff6b35; }
      }
    }
  }

  .footer {
    .pagectrl {
      display: flex;
      justify-content: center;
      margin-bottom: 15px;
      align-items: center;
      padding: 20px 0;

      .btn {
        padding: 0;
        margin: 0;
        border: none;
        width: 50px;
        height: 30px;
        background-color: #eee5de;
        border-radius: 5px;

        &:active {
          background-color: #e3e5e7;
        }
      }

      .pageNum {
        margin: 0 10px;
        color: #666;
        font-size: 14px;
      }
    }

    .empty {
      width: 100%;
      height: 400px;
      display: flex;
      justify-content: center;
      align-items: center;
      color: #999;
      font-size: 16px;
    }
  }

  // 底部导航占位
  .bottom-placeholder {
    height: 50px;
  }
}
</style>